import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

/**
 * Custom hook for managing schema configurations
 * Handles loading, initialization, and switching between schemas
 */
export const useTemplateManagement = ({
  locations,
  organisms,
  speciesByGroup,
  availableTemplates,
  setAvailableTemplates,
  selectedTemplate,
  customOrganisms,
  currentZooplanktonOrganisms,
  setCurrentLocations,
  setGridData,
  setZooplanktonGridData,
  calculateSums,
  isEditMode,
  draftLoadedRef
}) => {
  // Use refs for functions to avoid them being dependencies
  const calculateSumsRef = useRef(calculateSums);
  const setCurrentLocationsRef = useRef(setCurrentLocations);
  const setGridDataRef = useRef(setGridData);
  const setZooplanktonGridDataRef = useRef(setZooplanktonGridData);
  const isInitialMountRef = useRef(true);
  
  useEffect(() => {
    calculateSumsRef.current = calculateSums;
    setCurrentLocationsRef.current = setCurrentLocations;
    setGridDataRef.current = setGridData;
    setZooplanktonGridDataRef.current = setZooplanktonGridData;
  }, [calculateSums, setCurrentLocations, setGridData, setZooplanktonGridData]);
  
  // Load saved combinations on mount
  useEffect(() => {
    const savedCombinations = JSON.parse(localStorage.getItem('algae_combinations') || '{}');
    // Update speciesByGroup with saved combinations
    Object.keys(savedCombinations).forEach(group => {
      if (!speciesByGroup[group]) {
        speciesByGroup[group] = [];
      }
      savedCombinations[group].forEach(species => {
        if (!speciesByGroup[group].includes(species)) {
          speciesByGroup[group].push(species);
        }
      });
    });
    // Schema configs are loaded from backend in BulkSampleEntry.jsx
  }, [locations, organisms, speciesByGroup, setAvailableTemplates]);
  
  // Track if we just loaded from draft (to avoid re-initializing gridData)
  const justLoadedFromDraftRef = useRef(false);
  
  // Watch for schema config changes and update table structure
  useEffect(() => {
    
    // Skip processing on initial mount to prevent circular dependencies with organism fetching
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }
    
    // Skip if we just loaded from draft (wait for draft to fully settle)
    if (justLoadedFromDraftRef.current) {
      justLoadedFromDraftRef.current = false;
      return;
    }
    
    if (availableTemplates.length === 0) {
      // Initialize with defaults first time - but NOT if editing existing data or no template selected
      if (!isEditMode && selectedTemplate) {
        setCurrentLocationsRef.current(locations);
      }
      return;
    }
    
    const schemaConfig = availableTemplates.find(t => t.id === selectedTemplate);
    if (!schemaConfig || !schemaConfig.config) {
      // No matching template found - don't override locations
      return;
    }
    
    // Update from schema config - but NOT if editing existing data or draft loaded with its own data
    if (isEditMode) return;
    if (draftLoadedRef?.current) return;
    
    const schemaLocations = schemaConfig.config.locations.map(loc => loc.full_name);
    // Deduplicate locations to prevent duplicate columns
    const uniqueSchemaLocations = [...new Set(schemaLocations)];
    
    // Get organisms from schema + add SUM rows + customOrganisms
    const schemaOrganisms = schemaConfig.config.organisms || [];
    
    // Add SUM rows ONLY for Kiselalger group
    const organismsByGroup = {};
    schemaOrganisms.forEach(org => {
      if (!organismsByGroup[org.group]) organismsByGroup[org.group] = [];
      organismsByGroup[org.group].push(org);
    });
    
    const withSums = [];
    Object.entries(organismsByGroup).forEach(([group, orgs]) => {
      withSums.push(...orgs);
      // Add SUM row ONLY for Kiselalger
      if (group === 'Kiselalger') {
        withSums.push({
          group,
          species: `${group.toUpperCase()} SUM`,
          depth: '',
          isSum: true
        });
      }
    });
    
    // Combine schema organisms + custom organisms
    setCurrentLocationsRef.current(uniqueSchemaLocations);
    
    // DESIGN: We should NOT call fetchOrganisms here because that would override schema organisms
    // Instead, the parent component handles this by NOT calling fetchOrganisms when schema is selected
    // For now, we just set locations and let the gridData preserve existing organisms
    
    // Re-initialize gridData with new dimensions, but PRESERVE existing data if locations match
    setGridDataRef.current(prevGridData => {
      // Check if existing grid uses the same locations as the new template
      const firstOrgKey = Object.keys(prevGridData)[0];
      const existingLocs = firstOrgKey ? Object.keys(prevGridData[firstOrgKey] || {}) : [];
      const locsMatch = existingLocs.length > 0 && 
        existingLocs.length === uniqueSchemaLocations.length && 
        existingLocs.every(l => uniqueSchemaLocations.includes(l));
      
      if (locsMatch) {
        // Same locations - just recalculate sums
        return calculateSumsRef.current(prevGridData);
      }
      
      // Different locations (template changed) - reinitialize grid with new locations
      const newGridData = {};
      [...withSums, ...customOrganisms].forEach(org => {
        const key = `${org.group}_${org.species}`;
        newGridData[key] = {};
        uniqueSchemaLocations.forEach(loc => {
          newGridData[key][loc] = '';
        });
      });
      return calculateSumsRef.current(newGridData);
    });
    
    // Also update zooplankton grid for new locations
    setZooplanktonGridDataRef.current(prevZooplanktonData => {
      const newZooplanktonData = {};
      
      // Initialize all current organisms with new template locations
      currentZooplanktonOrganisms.forEach(org => {
        const key = `${org.sampleType}_${org.organism}`;
        newZooplanktonData[key] = {};
        uniqueSchemaLocations.forEach(loc => {
          // Preserve existing value if key matches
          newZooplanktonData[key][loc] = prevZooplanktonData[key]?.[loc] || '';
        });
      });
      
      return newZooplanktonData;
    });
    
    if (selectedTemplate !== 'default-template' && availableTemplates.length > 0) {
      toast.success(`Skjema oppdatert: ${schemaConfig.name}`);
    }
  }, [selectedTemplate, availableTemplates, customOrganisms, locations]);
  // DEPENDENCY INFO: Removed currentZooplanktonOrganisms from dependencies to prevent re-running when organisms are loaded from draft
};
